import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';

import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ConfirmationModal from 'hb-react/shared/components/DesignSystem/Modal/ConfirmationModal';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import Form from './Form';

const CheckInFormModal = ({
  checkInId,
  checkIn: checkInFromProps,
  onCloseModal,
  onCreate,
  onUpdate,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const isMobileDevice = useIsMobileDevice();

  const modalRef = useRef();
  const formRef = useRef();

  const [checkIn, setCheckIn] = useState(checkInFromProps || null);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);
  const [modalElement, setModalElement] = useState();
  const [isDisplayedEmojiPicker, setIsDisplayedEmojiPicker] = useState(false);

  const isUpdatingCheckIn = !!checkInId;
  const modalHeaderId = modalRef.current
    ?.getHeaderElement()
    ?.getAttribute('id');

  const canDisplayEmojiPicker = !isMobileDevice;

  const handleToggleEmojiPicker = (isToggled) => {
    setIsDisplayedEmojiPicker(isToggled);
  };

  // Update modalElement if modal updated
  useEffect(() => {
    setModalElement(modalRef.current?.getModalElement());
  }, [modalRef.current]);

  useEffect(() => {
    // Do not fetch if the check-in is already passed to the Component
    if (checkInFromProps) {
      return undefined;
    }

    if (isUpdatingCheckIn) {
      // Edit existing check-in
      axios
        .get(Routes.user_location_path(currentUser.id, checkInId))
        .then(({ data }) => {
          setCheckIn(data?.location);
        });
    } else {
      // Create draft for new check-in
      axios
        .get(Routes.new_user_location_path(currentUser.id))
        .then(({ data }) => {
          setCheckIn(data?.location);
        });
    }
  }, []);

  return (
    <>
      <ModalWithTemplate
        header={
          isUpdatingCheckIn
            ? __('Edit check-in')
            : __('Create a check-in · global feed')
        }
        isLoading={isEmpty(checkIn)}
        onCloseModal={() =>
          formRef.current?.canDisplayDiscardChangesModal()
            ? setDisplayCloseModal(true)
            : onCloseModal()
        }
        ref={modalRef}
        screenReadersCloseButtonText={
          isUpdatingCheckIn
            ? s__('Accessibility|Close edit a check-in window')
            : s__('Accessibility|Close create a check-in window')
        }
        enableEscAction={!isDisplayedEmojiPicker}
        // Needed to handle native share button
        pb={{ s: '85px', m: 6 }}
      >
        <Form
          canDisplayEmojiPicker={canDisplayEmojiPicker}
          checkIn={checkIn}
          isLoading={isEmpty(checkIn)}
          isUpdatingCheckIn={isUpdatingCheckIn}
          modalElement={modalElement}
          modalHeaderId={modalHeaderId}
          onCloseModal={onCloseModal}
          onCreate={onCreate}
          onToggleEmojiPicker={handleToggleEmojiPicker}
          onUpdate={onUpdate}
          ref={formRef}
        />
      </ModalWithTemplate>
      {displayCloseModal && (
        <ConfirmationModal
          onCloseModal={() => setDisplayCloseModal(false)}
          confirmButtonLabel={
            isUpdatingCheckIn
              ? s__('LiveFeed|Discard changes')
              : s__('LiveFeed|Discard draft')
          }
          onConfirmation={onCloseModal}
          text={
            isUpdatingCheckIn
              ? s__(
                  'LiveFeed|Your changes will be discarded. Are you sure you want to exit?',
                )
              : s__(
                  'LiveFeed|Your draft will be discarded. Are you sure you want to exit?',
                )
          }
          title={
            isUpdatingCheckIn
              ? s__('LiveFeed|Discard changes')
              : s__('LiveFeed|Discard draft')
          }
        />
      )}
    </>
  );
};

CheckInFormModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  checkInId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CheckInFormModal.defaultProps = {
  withCategories: false,
};

export default CheckInFormModal;
